import { useState } from 'react';
import { getFromHub, postToHub } from '../network/actions';
import { getMenu } from '../utils/menu';
import { setSessionCookie } from '../utils/auth';
import MfaSetupStep from './MfaSetupStep';
import MfaVerifyStep from './MfaVerifyStep';
import ChangePasswordStep from './ChangePasswordStep';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';

const STEP = {
    CREDENTIALS: 'credentials',
    MFA_SETUP: 'mfa-setup',
    MFA_VERIFY: 'mfa-verify',
    CHANGE_PASSWORD: 'change-password',
};

async function finalizeLogin(loginResponse, dispatch) {
    setSessionCookie(loginResponse.sessionId);
    const permissionsResponse = await getFromHub('authentication/profile');
    const navigation = getMenu(permissionsResponse.hubPermissions, permissionsResponse.isRiver);
    dispatch({ type: 'SET_USER', user: loginResponse, permissions: permissionsResponse.hubPermissions, navigation });
}

function LoginPage({ dispatch }) {
    const [step, setStep] = useState(STEP.CREDENTIALS);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loginMeta, setLoginMeta] = useState(null);

    const handleCredentialsSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                user: username,
                password,
            };

            const response = await postToHub('authentication/loginByApp/v2', payload);

            if (!response) {
                setError('Wrong username or password.');
                return;
            }

            setLoginMeta(response);
            setSessionCookie(response.sessionId);

            const needsPasswordChange =
                (response.passwordExpired && response.forcePasswordResetOnExpiry) ||
                (response.firstTimeLogin && response.forcePasswordResetOnFirstLogin);

            if (needsPasswordChange) {
                setStep(STEP.CHANGE_PASSWORD);
                return;
            }

            if (!response.isMfaRequired) {
                await finalizeLogin(response, dispatch);
                return;
            }

            if (response.requiresMfaRegistration) {
                setStep(STEP.MFA_SETUP);
            } else {
                setStep(STEP.MFA_VERIFY);
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEnrolled = () => {
        setStep(STEP.MFA_VERIFY);
    };

    const handleMfaAuthenticated = async (mfaResponse) => {
        await finalizeLogin(mfaResponse, dispatch);
    };

    const handlePasswordChanged = () => {
        setStep(STEP.CREDENTIALS);
    };

    const goBack = () => {
        setStep(STEP.CREDENTIALS);
        setLoginMeta(null);
        setError('');
    };

    const inputClassNames = "shadow appearance-none border border-red rounded w-full py-2 px-3 text-grey-700" + (error ? " border-red-700" : "");

    return (
        <div className="fixed inset-0 overflow-scroll">
            <img src={require(`../images/login_bg.jpg`).default} alt="" className="fixed inset-0 object-cover w-full min-h-full" />
            <div className="relative flex items-center justify-center w-full min-h-full">
                <div className="bg-white shadow-md rounded-lg p-4 sm:p-8 flex flex-col max-w-lg">
                    <div className="self-center">
                        <img className="w-48 mb-8" src={require('../images/digitech_logo.svg').default} alt="digitech logo" />
                    </div>

                    {step === STEP.CREDENTIALS && (
                        <>
                            <div className="uppercase text-2xl">
                                Sign in to the Hub Console
                            </div>
                            <form onSubmit={handleCredentialsSubmit} className="flex flex-col">
                                <div className="mt-4">
                                    <InputText
                                        className={inputClassNames}
                                        id="username"
                                        name="username"
                                        value={username}
                                        onChange={(e) => {
                                            setError('');
                                            setUsername(e.target.value);
                                        }}
                                        placeholder="Username"
                                        required
                                    />
                                </div>
                                <div className="mt-4">
                                    <Password
                                        id="password"
                                        name="password"
                                        value={password}
                                        onChange={(e) => {
                                            setError('');
                                            setPassword(e.target.value);
                                        }}
                                        placeholder="******************"
                                        className="w-full"
                                        inputClassName={inputClassNames}
                                        feedback={false}
                                        required
                                    />
                                </div>
                                {error &&
                                    <div className="mt-4 text-red-700">
                                        {error}
                                    </div>
                                }
                                <div className="mt-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
                                    >
                                        {loading ? 'Signing in…' : 'Sign In'}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}

                    {step === STEP.MFA_SETUP && (
                        <MfaSetupStep
                            onEnrolled={handleEnrolled}
                            onBack={goBack}
                        />
                    )}

                    {step === STEP.MFA_VERIFY && (
                        <MfaVerifyStep
                            showTrustOption={loginMeta?.showTrustThisDeviceOption ?? false}
                            onAuthenticated={handleMfaAuthenticated}
                            onBack={goBack}
                        />
                    )}

                    {step === STEP.CHANGE_PASSWORD && (
                        <ChangePasswordStep
                            isFirstLogin={loginMeta?.firstTimeLogin}
                            onPasswordChanged={handlePasswordChanged}
                            onBack={goBack}
                        />
                    )}

                </div>
            </div>
        </div>
    );
}

export default LoginPage;